// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ApiParameter, get, post, request } from "@insite/client-framework/Services/ApiService";
import { BaseModel } from "@insite/client-framework/Types/ApiModels";

export interface AccountLookupApiParameter extends ApiParameter {
    lookupByAccountNumber: boolean;
    email: string;
    accountNumber: string;
    street: string;
    state: string;
    city: string;
    zipCode: string;
}

export interface AccountLookupResult extends BaseModel {
    id: string;
    companyName: string;
}

export interface AutoAlignRegistrationApiParameter extends ApiParameter {
    email: string;
    firstName: string;
    lastName: string;
    department: string;
    billToId: string;
    acceptTerms: boolean;
    isSubscribed: boolean;
}

export interface BusinessAddress {
    id?: string; // used in frontend only
    label?: string; // used in frontend only
    address1: string;
    address2?: string;
    city: string;
    state: string;
    postalCode: string;
}

export interface ValidateAddressApiParameter extends BusinessAddress, ApiParameter {}

export interface ValidateAddressResult extends BaseModel {
    isValid: boolean;
    errorMessages: string[];
    normalizedAddresses: BusinessAddress[];
}

export interface DeclineUserAccountApiParameter extends ApiParameter {
    token: string;
}

export interface LoadTermsInfoParameter extends ApiParameter {
    userId: string;
}

export interface TermsInfoResult extends BaseModel {
    acceptTerms: string;
    acceptTermsTime: string;
    token: string;
    username: string;
}

export interface AcceptUserTermsParameter extends ApiParameter {
    username: string;
    token: string;
}

const customAccountUrl = "api/mcmc/account";

async function submitFormData(uri: string, formData: FormData) {
    const results = await request<any>(`${customAccountUrl}/${uri}`, "POST", {}, formData);
    return results;
}

export async function lookUpForAccount(parameter: AccountLookupApiParameter) {
    const results = await get<AccountLookupResult>(`${customAccountUrl}/accountNumberLookup`, parameter);
    return results;
}

export async function sendAutoAligRegistrationRequest(parameter: AutoAlignRegistrationApiParameter) {
    const results = await post<AutoAlignRegistrationApiParameter>(
        `${customAccountUrl}/autoAlignmentRegistration`,
        parameter,
    );
    return results;
}

export async function validateAddress(parameter: ValidateAddressApiParameter) {
    const results = await get<ValidateAddressResult>(`${customAccountUrl}/validateAddress`, parameter);
    return results;
}

export async function sendNewTermsCustomerRegistrationRequest(formData: FormData) {
    const results = await submitFormData("creditApplicationCustomerRegistration", formData);
    return results;
}

export async function submitCreditCardOnlyCustomerForm(formData: FormData) {
    const results = await submitFormData("creditCardOnlyRegistration", formData);
    return results;
}

export async function declineUserAccount(parameter: DeclineUserAccountApiParameter) {
    const results = await post<DeclineUserAccountApiParameter, string>(`${customAccountUrl}/declineUser`, parameter);
    return results;
}

export async function getTermsInfo(parameter: LoadTermsInfoParameter) {
    const results = await post<TermsInfoResult>(`${customAccountUrl}/getUserTermInfo?userId=${parameter.userId}`);
    return results;
}

export async function acceptUserTerms(parameter: AcceptUserTermsParameter) {
    const results = await post<AcceptUserTermsParameter, string>(`${customAccountUrl}/AcceptUserTerms`, parameter);
    return results;
}
